import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import StatsCounter from "./StatsCounter";
import { TrendingUp, TrendingDown, Phone, Clock, DollarSign, Smile } from "lucide-react";
import { useTranslation } from 'react-i18next';

const AnalyticsDashboardMockup = () => {
  const { t } = useTranslation();
  
  const stats = [
    { value: "2847", label: t('platform.dashboard_active_calls'), suffix: "" },
    { value: "94", label: t('platform.dashboard_satisfaction'), suffix: "%" },
    { value: "0.12", label: t('platform.dashboard_cost_call'), suffix: "$" },
    { value: "47", label: t('platform.dashboard_avg_duration'), suffix: "s" },
  ];

  const sentimentData = [
    { time: "10:00", positive: 75, neutral: 20, negative: 5 },
    { time: "11:00", positive: 80, neutral: 15, negative: 5 },
    { time: "12:00", positive: 70, neutral: 25, negative: 5 },
    { time: "13:00", positive: 85, neutral: 12, negative: 3 },
    { time: "14:00", positive: 78, neutral: 18, negative: 4 },
  ];

  const intentCategories = [
    { name: t('platform.dashboard_intent_billing'), value: 35, color: "bg-pink-500" },
    { name: t('platform.dashboard_intent_support'), value: 28, color: "bg-purple-500" },
    { name: t('platform.dashboard_intent_sales'), value: 22, color: "bg-blue-500" },
    { name: t('platform.dashboard_intent_technical'), value: 15, color: "bg-cyan-500" },
  ];

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="space-y-6"
      >
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Phone, label: t('platform.dashboard_active_calls'), value: "2,847", trend: "+12%" },
            { icon: Smile, label: t('platform.dashboard_satisfaction'), value: "94%", trend: "+5%" },
            { icon: DollarSign, label: t('platform.dashboard_cost_call'), value: "$0.12", trend: "-18%" },
            { icon: Clock, label: t('platform.dashboard_avg_duration'), value: "47s", trend: "-8%" },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="p-4 glass-effect border-primary/20">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs text-muted-foreground">{stat.label}</span>
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold">{stat.value}</span>
                  <span
                    className={`text-xs font-semibold flex items-center gap-1 ${
                      stat.trend.startsWith("+") ? "text-green-500" : "text-green-500"
                    }`}
                  >
                    {stat.trend.startsWith("+") ? (
                      <TrendingUp className="w-3 h-3" />
                    ) : (
                      <TrendingDown className="w-3 h-3" />
                    )}
                    {stat.trend}
                  </span>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Sentiment Timeline */}
        <Card className="p-6 glass-effect border-primary/20">
          <h3 className="font-bold mb-4">{t('platform.dashboard_sentiment_title')}</h3>
          <div className="space-y-2">
            {sentimentData.map((data, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center gap-3"
              >
                <span className="text-xs text-muted-foreground w-12">{data.time}</span>
                <div className="flex-grow h-8 flex rounded-lg overflow-hidden">
                  <div
                    className="bg-green-500 flex items-center justify-center text-xs font-semibold text-white"
                    style={{ width: `${data.positive}%` }}
                  >
                    {data.positive}%
                  </div>
                  <div
                    className="bg-yellow-500 flex items-center justify-center text-xs font-semibold text-white"
                    style={{ width: `${data.neutral}%` }}
                  >
                    {data.neutral}%
                  </div>
                  <div
                    className="bg-red-500 flex items-center justify-center text-xs font-semibold text-white"
                    style={{ width: `${data.negative}%` }}
                  >
                    {data.negative}%
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="flex gap-4 mt-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span>{t('platform.dashboard_sentiment_positive')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span>{t('platform.dashboard_sentiment_neutral')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span>{t('platform.dashboard_sentiment_negative')}</span>
            </div>
          </div>
        </Card>

        {/* Intent Classification */}
        <Card className="p-6 glass-effect border-primary/20">
          <h3 className="font-bold mb-4">{t('platform.dashboard_intent_title')}</h3>
          <div className="space-y-3">
            {intentCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{category.name}</span>
                  <span className="text-sm text-muted-foreground">{category.value}%</span>
                </div>
                <div className="h-3 bg-secondary rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${category.value}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                    className={`h-full ${category.color}`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </Card>

        {/* ROI Projection */}
        <Card className="p-6 glass-effect border-primary/20 bg-gradient-to-br from-primary/10 to-accent/10">
          <h3 className="font-bold mb-4">{t('platform.dashboard_roi_title')}</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t('platform.dashboard_roi_savings')}</p>
              <p className="text-3xl font-bold gradient-brand-text">$847,000</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">{t('platform.dashboard_roi_reduction')}</p>
              <p className="text-3xl font-bold text-green-500">-42%</p>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default AnalyticsDashboardMockup;
